/**
 * Small, dependency-free utilities shared across custom elements. Kept
 * intentionally tiny — per the brief, no global monolithic theme.js; each
 * feature gets its own module (cart.js, product-form.js, ...).
 */

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(null, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/** Focus trap for drawers/modals — Tab/Shift+Tab wrap within `container`. */
function trapFocus(container, elementToFocus = container) {
  const focusableSelector =
    'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe';
  const elements = Array.from(container.querySelectorAll(focusableSelector));
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  container.addEventListener('keydown', trapFocusHandler);

  function trapFocusHandler(event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  }

  container.__trapFocusHandler = trapFocusHandler;
  (elementToFocus || container).focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.querySelectorAll('[data-trap-focus]').forEach((container) => {
    if (container.__trapFocusHandler) {
      container.removeEventListener('keydown', container.__trapFocusHandler);
    }
  });
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement && summaryElement.setAttribute('aria-expanded', 'false');
  summaryElement && summaryElement.focus();
}

function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = (cents / 100).toFixed(2).replace('.', ',');
  return (format || '{{amount}}').replace('{{amount}}', value);
}

window.NantesUtils = {
  debounce,
  fetchConfig,
  trapFocus,
  removeTrapFocus,
  onKeyUpEscape,
  formatMoney,
};

document.addEventListener('keyup', onKeyUpEscape);
